import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getConversionLimitApi, getConversionRateApi } from '@flipster/apis/v1/wallet';
import { GetConversionLimitResponse, GetConversionRateResponse } from '@flipster/apis/v1/wallet/type';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { createStepsAtom } from '@flipster/react/misc/step/atom';
import Num, { BigNumber } from '@flipster/utils/num';

import { CurrencyDisplay } from '../../../misc/display';
import { isActionAllowedForRegulation } from '../../currency/is-action-allowed-for-regulation';
import { userRegulationBunja } from '../../regulation';
import { snapshotSpecBunja } from '../../snapshot/state';
import { walletModalBunja } from '../shared/core/state';

type Steps = {
  form: undefined;
  preview: undefined;
  complete: undefined;
};

export const convertModalBunja = bunja(() => {
  const Steps = createStepComponent<Steps>();
  const steps = createStepsAtom<Steps>({ step: 'form', context: undefined });

  const { selectedCurrencyAtom } = bunja.use(walletModalBunja);
  const { walletsAtom } = bunja.use(walletsBunja);
  const { snapshotSpecAtom, currenciesKeyByCurrencyAtom } = bunja.use(snapshotSpecBunja);
  const { getUserRegulationAtom } = bunja.use(userRegulationBunja);

  const fromCurrencyAtom = atom((get) => get(selectedCurrencyAtom));
  const toCurrencyAtom = atom<string | undefined>();

  // useConvertPrecision
  const precisionAtom = atom((get) => {
    const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
    const from = get(fromCurrencyAtom) ?? '';
    const to = get(toCurrencyAtom) ?? '';
    return {
      from: currenciesKeyByCurrency?.[from]?.fullDisplayPrecision ?? 8,
      to: currenciesKeyByCurrency?.[to]?.fullDisplayPrecision ?? 8,
    };
  });
  // useConvert
  const convertibleLimitBaseAtom = atomWithQuery((get) => {
    const from = get(fromCurrencyAtom);
    const to = get(toCurrencyAtom);
    return {
      queryKey: ['getConversionLimitApi', from, to],
      queryFn: () => getConversionLimitApi(from!, to!),
      select: (res: AxiosResponse<GetConversionLimitResponse>) => res.data,
      enabled: !!from && !!to,
    };
  });
  const convertibleLimitAtom = atom((get) => get(convertibleLimitBaseAtom).data);
  const toItemsAtom = atom((get) => {
    const from = get(fromCurrencyAtom);
    const currencies = get(snapshotSpecAtom)?.currencies;
    const regulationStatus = get(getUserRegulationAtom).data?.regulationStatus ?? null;
    return (currencies ?? [])
      .filter(
        (c) =>
          c.currency !== from && isActionAllowedForRegulation(c.conversionAllowedRegulationStatuses, regulationStatus),
      )
      .map((c) => ({
        label: (
          <CurrencyDisplay
            currency={c.currency}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={<CurrencyDisplay.Label value='shortName' css={{ textStyle: 'Text/M/Strong' }} />}
          />
        ),
        value: c.currency,
      }));
  });
  const walletBalanceWithdrawableAtom = atom(
    (get) => get(walletsAtom)?.[get(fromCurrencyAtom) ?? '']?.walletBalanceWithdrawable,
  );
  const conversionPreviewBaseAtom = atomWithQuery((get) => {
    const from = get(fromCurrencyAtom);
    const to = get(toCurrencyAtom);
    return {
      queryKey: ['getConversionRateApi', from, to],
      queryFn: () =>
        getConversionRateApi({
          fromCurrency: from!,
          toCurrency: to!,
          currencyConversionType: 'CURRENCY_CONVERSION_TYPE_V2',
        }),
      select: (res: AxiosResponse<GetConversionRateResponse>) => res.data,
      enabled: !!from && !!to,
      refetchInterval: 1000, // 1초마다 polling
    };
  });

  const exchangeRateAtom = atom((get) => get(conversionPreviewBaseAtom).data?.exchangeRate);
  const fromMinAmountAtom = atom((get) => {
    const exchangeRate = get(exchangeRateAtom);
    const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);
    const from = get(fromCurrencyAtom) ?? '';
    const to = get(toCurrencyAtom) ?? '';
    const fromUnit = currenciesKeyByCurrency?.[from]?.conversionUnit ?? '0.0001';
    const toUnit = currenciesKeyByCurrency?.[to]?.conversionUnit ?? '0.0001';
    if (!exchangeRate) return fromUnit;
    if (Num(fromUnit).multipliedBy(exchangeRate).isGreaterThanOrEqualTo(toUnit)) return fromUnit;
    return Num(toUnit).dividedBy(exchangeRate).toFixed(get(precisionAtom).from).toString();
  });
  /**
   * https://www.notion.so/prextech/Asset-Conversion-0ed999d4bd9c43458235fabb9b6591ab#2a98d96df8ea420186ff3c6fb483ed81
   * convert modal에서 최대로 convert할 수 있는 amount를 계산하는 함수.(from, to)
   * min(convert가능한 fromAount, convertibleLimit.remainingLimit)을 계산하여 반환
   * 여기서 convertibleLimit.remainingLimit은 min(one-time conversion limit, 24H conversion limit)을 의미
   * */
  const convertibleLimitBalanceAtom = atom((get) => {
    const exchangeRate = get(exchangeRateAtom);
    const convertibleLimit = get(convertibleLimitAtom);
    const walletBalanceWithdrawable = get(walletBalanceWithdrawableAtom);
    const to = get(toCurrencyAtom);
    if (!convertibleLimit || !exchangeRate || !walletBalanceWithdrawable) return undefined;

    const returnLimit = {
      fromCurrency: '',
      toCurrency: '',
    };

    // from walletBalanceWithdrawable
    const fromExclBalance = Num(walletBalanceWithdrawable);
    // from currency단위의 convertibleLimit.remainingLimit
    const fromRemainingLimit =
      to === convertibleLimit.currency
        ? Num(convertibleLimit.remainingLimit).dividedBy(exchangeRate)
        : Num(convertibleLimit.remainingLimit);

    returnLimit.fromCurrency = BigNumber.min(fromExclBalance, fromRemainingLimit).toString();

    // from walletBalanceWithdrawable to로 변환한 값
    const toExclBalance = Num(walletBalanceWithdrawable).multipliedBy(exchangeRate);
    // to currency단위의 convertibleLimit.remainingLimit
    const toRemainingLimit =
      to === convertibleLimit.currency
        ? Num(convertibleLimit.remainingLimit)
        : Num(convertibleLimit.remainingLimit).multipliedBy(exchangeRate);

    returnLimit.toCurrency = BigNumber.min(toExclBalance, toRemainingLimit).toString();

    return returnLimit;
  });
  const convertibleLimitCurrencyAtom = atom((get) => get(convertibleLimitAtom)?.currency);

  return {
    // step
    Steps,
    steps,

    selectedCurrencyAtom,
    fromCurrencyAtom,
    toCurrencyAtom,
    // useConvertPrecision
    precisionAtom,
    // useConvert
    toItemsAtom,
    convertibleLimitAtom,
    convertibleLimitBalanceAtom,
    convertibleLimitCurrencyAtom,
    fromMinAmountAtom,
    exchangeRateAtom,
    walletBalanceWithdrawableAtom,
  };
});
convertModalBunja.debugLabel = 'convertModalBunja';
